import { api, Response } from '../../function/axios'
import { QrRegistrationResult } from '../../store/qrRegistration/actionType'

/*
 * QR registration links for the v2 page: their status (the admin's switch and
 * the validity window together — as AuthService#validateQrRegistration
 * checks them), the time to the next change, and the printed poster as a PDF
 * blob (GET /auth/qr-registration/:id/pdf, rendered by the backend).
 */

export type QrStatus = 'active' | 'scheduled' | 'expired' | 'inactive'

export const STATUS_LABEL: Record<QrStatus, string> = {
  active: 'Active', scheduled: 'Scheduled', expired: 'Expired', inactive: 'Deactivated',
}
export const STATUS_COLOR: Record<QrStatus, string> = {
  active: 'var(--ok)', scheduled: 'var(--warn)', expired: 'var(--danger)', inactive: 'var(--text3)',
}

export function qrStatus(row: QrRegistrationResult, now = Date.now()): QrStatus {
  if (!row.isActive) return 'inactive'
  if (row.validFrom && now < new Date(row.validFrom).getTime()) return 'scheduled'
  if (row.validUntil && now > new Date(row.validUntil).getTime()) return 'expired'
  return 'active'
}

function span(ms: number): string {
  const m = Math.round(ms / 60000)
  if (m < 60) return `${Math.max(1, m)} min`
  const h = Math.round(m / 60)
  if (h < 48) return `${h} h`
  return `${Math.round(h / 24)} days`
}

/** "ends in 3 days", "starts in 5 h", "ended 2 days ago" — or nothing when there is no boundary ahead */
export function qrWhen(row: QrRegistrationResult, now = Date.now()): string | null {
  const st = qrStatus(row, now)
  if (st === 'scheduled' && row.validFrom) return `starts in ${span(new Date(row.validFrom).getTime() - now)}`
  if (st === 'active' && row.validUntil) return `ends in ${span(new Date(row.validUntil).getTime() - now)}`
  if (st === 'expired' && row.validUntil) return `ended ${span(now - new Date(row.validUntil).getTime())} ago`
  return null
}

/** the poster's validity line, worded and formatted as the backend prints it */
export function validityLine(validFrom?: string | null, validUntil?: string | null): string {
  const f = (v: string) => {
    const d = new Date(v)
    return `${d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
  }
  if (validFrom && validUntil) return `Valid ${f(validFrom)} – ${f(validUntil)}`
  if (validUntil) return `Valid until ${f(validUntil)}`
  if (validFrom) return `Valid from ${f(validFrom)}`
  return ''
}

export async function fetchQrPdf(id: number): Promise<Blob> {
  const { data }: Response = await api({ method: 'GET', url: `/auth/qr-registration/${id}/pdf`, responseType: 'blob' })
  return new Blob([data], { type: 'application/pdf' })
}
